<template>
  <div
    ref="root"
    class="time-picker-field"
    :style="themeStyles"
  >
    <input
      ref="input"
      class="time-picker-field__input"
      type="text"
      :value="displayValue"
      :placeholder="settings.placeholder"
      :disabled="disabled"
      readonly
      @click="open"
    />

    <div
      v-if="isOpen"
      ref="dropdown"
      class="time-picker-field__dropdown"
      :class="`time-picker-field__dropdown--${dropDirection}`"
      :style="dropdownStyle"
    >
      <infinite-time-picker
        v-model="draftTime"
        :minute-interval="settings.minuteInterval"
        :available-times="availableTimes"
      />
      <button
        type="button"
        class="time-picker-field__apply"
        @click="apply"
      >
        {{ settings.applyLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import InfiniteTimePicker from './InfiniteTimePicker.vue';
import { createTimePickerConfig, getPickerThemeStyles } from './config';
import { formatLegacyTime, normalizeTime, parseTime } from './utils';

export default {
  name: 'TimePickerField',

  components: {
    InfiniteTimePicker,
  },

  props: {
    value: {
      type: String,
      required: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    availableTimes: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    legacyFormat: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      dropDirection: 'down',
      committedValue: this.value,
      draftTime: this.normalizeIncoming(this.value),
    };
  },

  computed: {
    settings() {
      return createTimePickerConfig(this.config);
    },

    themeStyles() {
      return getPickerThemeStyles(this.settings);
    },

    dropdownStyle() {
      return {
        zIndex: this.settings.dropdownZIndex,
      };
    },

    displayValue() {
      return this.formatDisplayValue(this.committedValue);
    },
  },

  watch: {
    value(nextValue) {
      this.committedValue = nextValue;
      this.draftTime = this.normalizeIncoming(nextValue);
    },

    isOpen(open) {
      if (open) {
        document.addEventListener('mousedown', this.onClickOutside);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.updateDropDirection);
        window.addEventListener('scroll', this.updateDropDirection, true);
      } else {
        document.removeEventListener('mousedown', this.onClickOutside);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.updateDropDirection);
        window.removeEventListener('scroll', this.updateDropDirection, true);
      }
    },
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onClickOutside);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.updateDropDirection);
    window.removeEventListener('scroll', this.updateDropDirection, true);
  },

  methods: {
    normalizeIncoming(value) {
      return value ? normalizeTime(value) : '';
    },

    formatDisplayValue(value) {
      if (!value) {
        return '';
      }

      if (!this.legacyFormat) {
        return normalizeTime(value);
      }

      const { hour, minute } = parseTime(value);
      return formatLegacyTime(hour, minute);
    },

    open() {
      if (this.disabled) return;

      this.draftTime = this.normalizeIncoming(this.committedValue || this.value) || this.getFallbackDraftTime();
      this.isOpen = true;

      this.$nextTick(() => {
        this.updateDropDirection();
      });
    },

    updateDropDirection() {
      const input = this.$refs.input;
      const dropdown = this.$refs.dropdown;

      if (!input || !dropdown) {
        return;
      }

      const gap = 6;
      const inputRect = input.getBoundingClientRect();
      const dropdownHeight = dropdown.offsetHeight;
      const spaceBelow = window.innerHeight - inputRect.bottom - gap;
      const spaceAbove = inputRect.top - gap;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        this.dropDirection = 'up';
      } else {
        this.dropDirection = 'down';
      }
    },

    getFallbackDraftTime() {
      if (this.committedValue || this.value) {
        return this.normalizeIncoming(this.committedValue || this.value);
      }

      if (this.availableTimes && this.availableTimes.length) {
        return normalizeTime(this.availableTimes[0]);
      }

      return '';
    },

    close() {
      this.isOpen = false;
    },

    apply() {
      const { hour, minute } = parseTime(this.draftTime);
      const nextValue = this.legacyFormat
        ? formatLegacyTime(hour, minute)
        : normalizeTime(this.draftTime);

      this.committedValue = nextValue;
      this.$emit('input', nextValue);
      this.close();
    },

    onClickOutside(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.close();
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
  },
};
</script>

<style scoped>
.time-picker-field {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 100%;
}

.time-picker-field__input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  color: #111827;
  background: #fff;
  cursor: pointer;
  box-shadow: none;
}

.time-picker-field__input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.time-picker-field__input:focus {
  outline: none;
  border-color: #9ca3af;
  box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.25);
}

.time-picker-field__dropdown {
  position: absolute;
  left: 0;
  min-width: 220px;
  padding: 12px 12px 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
}

.time-picker-field__dropdown--down {
  top: calc(100% + 6px);
  bottom: auto;
}

.time-picker-field__dropdown--up {
  bottom: calc(100% + 6px);
  top: auto;
}

.time-picker-field__apply {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: var(--tp-apply-bg, #111827);
  color: var(--tp-apply-text, #fff);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.time-picker-field__apply:hover {
  background: var(--tp-apply-bg-hover, #1f2937);
}
</style>
